/* Sprint 2R-3B — Driver-facing follow-up warning + conversation booking. */
import React from "react";
import { driverApi } from "../lib/api";
import { Loader2, AlertOctagon, Calendar, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";

const MSG_BY_TRIGGER = {
  complaint_threshold: "Der er registreret gentagne driftsudfordringer på dine leveringer. Du skal booke en samtale med EMA Kurer, så vi kan gennemgå årsagen og aftale næste skridt.",
  return_rate: "Der er registreret et højt antal returpakker på dine ruter. Book venligst en samtale med EMA Kurer.",
  failed_delivery_rate: "Der er registreret et højt antal forgæves leveringer på dine ruter. Book venligst en samtale med EMA Kurer.",
  missed_deadline: "Du har flere ruter eller leveringer, hvor deadline ikke er overholdt. Book venligst en samtale med EMA Kurer.",
  late_start: "Du har flere sene rute-starter. Book venligst en samtale med EMA Kurer.",
  no_show: "No-show registreret. Book venligst en samtale med EMA Kurer.",
  scan_mismatch: "Flere scan-mismatches registreret. Book venligst en samtale med EMA Kurer.",
  rule_violation: "Gentagne regelbrud registreret. Book venligst en samtale med EMA Kurer.",
};

const TIPS = [
  "Tjek altid pakkens label-nummer ved scan",
  "Ring til kunden hvis du er forsinket > 15 minutter",
  "Brug photo proof ved hver levering",
  "Meld no-show senest 30 min før vagtstart",
];

export default function DriverFollowupPage() {
  const [cases, setCases] = React.useState([]);
  const [convs, setConvs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [activeCase, setActiveCase] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [c, cv] = await Promise.all([
        driverApi().get("/driver/followup/cases/mine"),
        driverApi().get("/driver/followup/conversations/mine"),
      ]);
      setCases(c.data.items || []);
      setConvs(cv.data.items || []);
    } catch {
      toast.error("Kunne ikke hente sager");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center gap-2 text-brand-text2 p-6">
      <Loader2 className="animate-spin" size={18}/> Indlæser...
    </div>
  );

  return (
    <div className="space-y-4 p-4 max-w-2xl mx-auto" data-testid="driver-followup-page">
      <div>
        <h1 className="font-display font-bold text-2xl text-brand-dark flex items-center gap-2">
          <AlertOctagon size={22} className="text-brand-red"/> Opfølgningssager
        </h1>
      </div>

      {cases.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 p-6 text-center" data-testid="driver-followup-empty">
          <div className="text-emerald-700 font-semibold">Ingen aktive opfølgningssager 🎉</div>
          <div className="text-sm text-emerald-600 mt-1">Bliv ved med det gode arbejde!</div>
        </div>
      ) : (
        cases.map((c) => (
          <div key={c.case_id} className="bg-white border-2 border-brand-red p-4"
               data-testid={`driver-case-${c.case_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="font-display font-bold text-brand-dark">{c.trigger_type.replace(/_/g, " ")}</div>
              <span className={`text-[10px] uppercase font-bold px-2 py-1 ${c.severity === "high_risk" || c.severity === "blocked_pending_review" ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>
                {c.severity}
              </span>
            </div>
            <p className="text-sm text-brand-dark mb-3">{MSG_BY_TRIGGER[c.trigger_type] || "Du har en aktiv opfølgningssag."}</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-brand-text2 mb-3">
              <div>Værdi: <b className="text-brand-dark">{c.trigger_value}</b></div>
              <div>Tærskel: <b className="text-brand-dark">{c.threshold_value}</b></div>
              <div>Periode: <b className="text-brand-dark">{c.trigger_period_days} dage</b></div>
              <div>Status: <b className="text-brand-dark">{c.status}</b></div>
            </div>
            {c.status !== "conversation_requested" && c.status !== "conversation_booked" ? (
              <button onClick={() => setActiveCase(c)}
                      className="w-full px-4 py-3 bg-brand-red hover:bg-red-700 text-white font-semibold inline-flex items-center justify-center gap-2"
                      data-testid={`driver-book-conv-${c.case_id}`}>
                <MessageSquare size={16}/> Book samtale med EMA Kurer
              </button>
            ) : (
              <div className="text-sm text-blue-700 inline-flex items-center gap-1">
                <Calendar size={14}/> Samtale {c.status === "conversation_booked" ? "bekræftet" : "anmodet"}
              </div>
            )}
          </div>
        ))
      )}

      {convs.length > 0 && (
        <div className="bg-white border border-brand p-4 mt-6">
          <h2 className="font-display font-bold text-base mb-2">Dine samtaler</h2>
          {convs.map((cv) => (
            <div key={cv.id} className="border-b border-gray-100 py-2 text-sm" data-testid={`driver-conv-${cv.id}`}>
              <div className="flex items-center justify-between">
                <span>Status: <b>{cv.status}</b></span>
                {cv.proposed_time_iso && <span className="text-xs text-brand-text2">Foreslået: {new Date(cv.proposed_time_iso).toLocaleString("da-DK")}</span>}
              </div>
              {cv.status === "proposed_time_sent" && (
                <button onClick={async () => {
                  try {
                    await driverApi().post(`/driver/followup/conversations/${cv.id}/confirm`,
                      { confirmed_time_iso: cv.proposed_time_iso });
                    toast.success("Samtale bekræftet");
                    load();
                  } catch (e) {
                    const d = e?.response?.data?.detail;
                    toast.error(typeof d === "string" ? d : (e?.message || "Fejl"));
                  }
                }} className="mt-1 text-xs px-3 py-1 bg-emerald-600 text-white"
                  data-testid={`driver-conv-confirm-${cv.id}`}>
                  Bekræft foreslået tidspunkt
                </button>
              )}            </div>
          ))}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 p-4">
        <div className="text-xs font-bold text-blue-900 uppercase mb-2">Forbedrings-tips</div>
        <ul className="text-xs text-blue-900 space-y-1 list-disc pl-4">
          {TIPS.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      </div>

      {activeCase && <BookConversationModal caseDoc={activeCase}
                                              onClose={() => setActiveCase(null)}
                                              onDone={() => { setActiveCase(null); load(); }}/>}
    </div>
  );
}

function BookConversationModal({ caseDoc, onClose, onDone }) {
  const [slot, setSlot] = React.useState("");
  const [comment, setComment] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    if (!slot) return toast.error("Vælg ønsket tidspunkt");
    setBusy(true);
    try {
      await driverApi().post("/driver/followup/conversations/request",
        { case_id: caseDoc.case_id, preferred_slots: [new Date(slot).toISOString()],
          comment: comment || undefined });
      toast.success("Anmodning sendt — EMA Kurer vender tilbage med tidspunkt");
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (e?.message || "Fejl"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full max-w-md mx-4 mb-4 md:mb-0 border border-brand p-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-bold text-lg mb-3">Book samtale</h3>
        <label className="block text-xs uppercase text-brand-text2 mb-1">Ønsket tidspunkt</label>
        <input type="datetime-local" value={slot} onChange={(e) => setSlot(e.target.value)}
               className="w-full px-3 py-2 border border-brand text-sm mb-3"
               data-testid="book-conv-slot"/>
        <label className="block text-xs uppercase text-brand-text2 mb-1">Kommentar (valgfri)</label>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)}
                  rows={3} className="w-full px-3 py-2 border border-brand text-sm mb-3"
                  data-testid="book-conv-comment"/>
        <div className="flex gap-2">
          <button onClick={submit} disabled={busy}
                  className="flex-1 px-4 py-3 bg-brand-red hover:bg-red-700 text-white font-semibold disabled:opacity-50"
                  data-testid="book-conv-submit">
            <Send size={14} className="inline mr-1"/> Send anmodning
          </button>
          <button onClick={onClose} className="px-4 py-3 bg-white border border-brand">Annuller</button>
        </div>
      </div>
    </div>
  );
}
